<script lang="ts">
    import { link, push } from 'svelte-spa-router';
    import { onMount } from 'svelte';
    import { api } from '../js/api.js';
    import { auth } from '../js/auth.js';
    import { toast } from '../js/toast.js';
    import { AuthLayout, AuthPasswordInput, AuthPrimaryButton } from './app';

    let email = '';
    let password = '';
    let errors: Record<string, string> = {};
    let submitting = false;
    let emailEl: HTMLInputElement;

    onMount(() => {
        const prefill = localStorage.getItem('prefill_email');
        if (prefill) {
            email = prefill;
            localStorage.removeItem('prefill_email');
        }
        emailEl?.focus();
    });

    async function login() {
        if (!validateForm()) return;
        submitting = true;
        try {
            await auth.login(email, password);
            toast.showNotification($auth.success, $auth.message, 'top');
            if (!$auth.success) return;
            await api.getResourceLists();
            if ($auth.success) {
                const pendingInvite = localStorage.getItem('pending_team_invite');
                if (pendingInvite) push(`/team-invite/${pendingInvite}`);
                else push(auth.userIsAdmin() ? '/dashboard-admin' : '/dashboard');
            }
        } finally {
            submitting = false;
        }
    }

    function validateForm() {
        errors = {};
        let valid = true;
        if (!email.trim()) { errors.email = 'Email or username is required'; valid = false; }
        if (!password) { errors.password = 'Password is required'; valid = false; }
        return valid;
    }

    $: if (errors.email && email) errors.email = '';
    $: if (errors.password && password) errors.password = '';
</script>

<AuthLayout title="Welcome back" subtitle="Sign in to your account to continue">
    <form class="auth-form" on:submit|preventDefault={login} novalidate>
        <div class="auth-form-field" class:error={!!errors.email}>
            <label class="auth-form-label" for="email">Email or username</label>
            <input
                bind:this={emailEl}
                id="email"
                class="auth-form-input"
                type="text"
                inputmode="text"
                autocomplete="username"
                spellcheck="false"
                bind:value={email}
                placeholder="admin or you@company.com"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {#if errors.email}
                <span id="email-error" class="auth-error-message" role="alert">{errors.email}</span>
            {/if}
        </div>

        <AuthPasswordInput
            id="password"
            label="Password"
            bind:value={password}
            placeholder="Enter your password"
            autocomplete="current-password"
            error={errors.password}
        >
            <a use:link href="/reset-password" slot="label-action" class="forgot-password-link">Forgot password?</a>
        </AuthPasswordInput>

        <AuthPrimaryButton loading={submitting} loadingLabel="Signing in…">
            Sign in
        </AuthPrimaryButton>
    </form>

    <p slot="footer">
        Don't have an account?
        <a use:link href="/signup" class="auth-inline-link">Create account</a>
    </p>
</AuthLayout>
